/**
 * Off-schedule-start note (Task 9b): when a session was actually STARTED on
 * a different calendar day than scheduled, or notably before/after its
 * scheduled start, surface a short note in the live-session header so staff
 * can see at a glance that "started" doesn't mean "started on time".
 *
 * Read-only surfacing of existing fields (startedAt, date, scheduledStart) —
 * no backend, no stored data.
 */
import { dublinDateString } from '../../utils/date';
import { SessionShape } from '../../types/attendance-v2';
import { dublinDateTimeMs } from './dublin-time';

/**
 * How far (in minutes) an actual start may drift from the scheduled start,
 * same calendar day, before it's called out as off-schedule.
 */
const OFF_SCHEDULE_THRESHOLD_MINUTES = 15;

/** "HH:MM" (24-hour) for the given instant, in Europe/Dublin. */
function dublinTimeString(instant: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Dublin',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(instant);
}

/**
 * "Weekday D Mon" (e.g. "Tuesday 18 Aug") for a "YYYY-MM-DD" civil date, in
 * Europe/Dublin. Built from formatToParts rather than a single
 * toLocaleDateString call — `en-IE`'s long-weekday format inserts a comma
 * ("Tuesday, 18 Aug") that the spec's "<Weekday D Mon>" shape doesn't want.
 */
function dublinWeekdayDateString(dateStr: string): string {
  // Noon on that calendar date, Dublin — safely inside the same Dublin day
  // regardless of DST, mirroring the anchor lateness.ts uses to resolve an
  // offset for a given civil date.
  const instant = new Date(dublinDateTimeMs(dateStr, '12:00'));
  const parts = new Intl.DateTimeFormat('en-IE', {
    timeZone: 'Europe/Dublin',
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  }).formatToParts(instant);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')}`;
}

type OffScheduleSession = Pick<SessionShape, 'date' | 'scheduledStart' | 'startedAt'>;

/**
 * Computes the off-schedule note for a session, or undefined when it started
 * on-schedule (or hasn't started yet).
 *
 * Triggers when the Dublin calendar day of `startedAt` differs from
 * `session.date`, OR the actual start is `OFF_SCHEDULE_THRESHOLD_MINUTES` or
 * more away from the scheduled start (either direction).
 */
export function offScheduleNote(session: OffScheduleSession): { copy: string } | undefined {
  if (!session.startedAt) return undefined;

  const startedInstant = new Date(session.startedAt);
  const startedDay = dublinDateString(startedInstant);
  const scheduledMs = dublinDateTimeMs(session.date, session.scheduledStart);
  const deltaMinutes = Math.round((startedInstant.getTime() - scheduledMs) / 60_000);

  if (startedDay !== session.date) {
    return {
      copy: `⏱ Started ${dublinWeekdayDateString(startedDay)} ${dublinTimeString(
        startedInstant
      )} (scheduled ${dublinWeekdayDateString(session.date)})`,
    };
  }

  if (Math.abs(deltaMinutes) < OFF_SCHEDULE_THRESHOLD_MINUTES) {
    return undefined;
  }

  const direction = deltaMinutes < 0 ? 'before' : 'after';
  return {
    copy: `⏱ Started ${dublinTimeString(startedInstant)} · ${Math.abs(
      deltaMinutes
    )} min ${direction} scheduled`,
  };
}
